import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { getFirestore, doc, getDoc, collection, addDoc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AddJobPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    locationType: 'onsite',
    applicationUrl: '',
    description: '',
    notes: '',
    responsibilities: '',
    requiredSkills: '',
    preferredSkills: '',
    experienceLevel: 'mid',
    salary: '',
    benefits: '',
    industry: '',
    roleType: 'technical',
    workEnvironment: 'onsite',
    applicationDeadline: '',
    fastTracked: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    // Clear fetch error if application URL is changed
    if (name === 'applicationUrl' && fetchError) {
      setFetchError('');
    }
  };

  const handleFetchJobDetails = async () => {
    // Validate URL first
    if (!formData.applicationUrl.trim()) {
      setFetchError('Please enter a job URL first');
      return;
    }
    
    if (!/^https?:\/\/.+/i.test(formData.applicationUrl)) {
      setFetchError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setIsFetchingDetails(true);
    setFetchError('');
    
    try {
      const functions = getFunctions();
      const extractJobDetails = httpsCallable(functions, 'extractJobDetails');
      
      const result = await extractJobDetails({ jobUrl: formData.applicationUrl });
      const jobDetails = result.data;
      
      console.log('Extracted job details:', jobDetails);
      
      // Update form with extracted data
      const updatedFormData = {
        ...formData,
        title: jobDetails.title || formData.title,
        company: jobDetails.company || formData.company,
        location: jobDetails.location || formData.location,
        locationType: jobDetails.locationType || formData.locationType,
        description: jobDetails.description || formData.description,
        responsibilities: Array.isArray(jobDetails.responsibilities) 
          ? jobDetails.responsibilities.join(', ') 
          : formData.responsibilities,
        requiredSkills: Array.isArray(jobDetails.requiredSkills) 
          ? jobDetails.requiredSkills.join(', ') 
          : formData.requiredSkills,
        preferredSkills: Array.isArray(jobDetails.preferredSkills) 
          ? jobDetails.preferredSkills.join(', ') 
          : formData.preferredSkills,
        experienceLevel: jobDetails.experienceLevel || formData.experienceLevel,
        salary: jobDetails.salary || formData.salary,
        benefits: Array.isArray(jobDetails.benefits) 
          ? jobDetails.benefits.join(', ') 
          : formData.benefits,
        industry: jobDetails.industry || formData.industry,
        roleType: jobDetails.roleType || formData.roleType,
        workEnvironment: jobDetails.workEnvironment || formData.workEnvironment,
        applicationDeadline: jobDetails.applicationDeadline || formData.applicationDeadline,
      };
      
      setFormData(updatedFormData);
      
    } catch (error) {
      console.error('Error fetching job details:', error);
      setFetchError(`Failed to extract job details: ${error.message}`);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Job title is required';
    }
    
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    }
    
    if (!formData.applicationUrl.trim()) {
      errors.applicationUrl = 'Application URL is required';
    } else if (!/^https?:\/\/.+/i.test(formData.applicationUrl)) {
      errors.applicationUrl = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const prepareArrayField = (field) => {
    if (!field) return [];
    return field.split(',').map(item => item.trim()).filter(item => item !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || !user) return;
    
    setIsLoading(true);
    try {
      const db = getFirestore();
      
      const jobData = {
        ...formData,
        titleLowercase: formData.title.toLowerCase().split(' '),
        createdBy: user.uid,
        creatorName: user.displayName || 'Anonymous User',
        creatorEmail: user.email,
        postedDate: new Date().toISOString(),
        isActive: true,
        responsibilities: prepareArrayField(formData.responsibilities),
        requiredSkills: prepareArrayField(formData.requiredSkills),
        preferredSkills: prepareArrayField(formData.preferredSkills),
        benefits: prepareArrayField(formData.benefits)
      };
      
      const jobRef = await addDoc(collection(db, 'jobs'), jobData);
      
      const userProfileRef = doc(db, 'user_profiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        await updateDoc(userProfileRef, {
          savedJobs: arrayUnion(jobRef.id)
        });
      } else {
        await setDoc(userProfileRef, {
          savedJobs: [jobRef.id],
          email: user.email,
          displayName: user.displayName || 'Anonymous User',
          createdAt: new Date().toISOString()
        });
      }
      
      navigate('/dashboard');
      
    } catch (err) {
      console.error("Error adding job:", err);
      setFormErrors({
        ...formErrors,
        general: `Failed to add job: ${err.message}. Please try again.`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl transform transition-all w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Add Job
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={() => navigate('/dashboard')}
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="animate-pulse flex flex-col space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {formErrors.general && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{formErrors.general}</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Job Title*
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
                {formErrors.title && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company*
                </label>
                <input
                  type="text"
                  name="company"
                  id="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
                {formErrors.company && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.company}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  id="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="locationType" className="block text-sm font-medium text-gray-700">
                  Work Type
                </label>
                <select
                  id="locationType"
                  name="locationType"
                  value={formData.locationType}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <option value="onsite">On-site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="applicationUrl" className="block text-sm font-medium text-gray-700">
                  Job Application URL <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    id="applicationUrl"
                    name="applicationUrl"
                    required
                    value={formData.applicationUrl}
                    onChange={handleChange}
                    className={`flex-grow block w-full min-w-0 rounded-l-md sm:text-sm border ${
                      formErrors.applicationUrl || fetchError ? 'border-red-300' : 'border-gray-300'
                    } focus:ring-purple-500 focus:border-purple-500`}
                    placeholder="https://example.com/jobs/123"
                  />
                  <button
                    type="button"
                    onClick={handleFetchJobDetails}
                    disabled={isFetchingDetails || !formData.applicationUrl}
                    className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50"
                  >
                    {isFetchingDetails ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Fetching...
                      </>
                    ) : (
                      'Fetch Details'
                    )}
                  </button>
                </div>
                {(formErrors.applicationUrl || fetchError) && (
                  <p className="mt-2 text-sm text-red-600">
                    {formErrors.applicationUrl || fetchError}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Enter the URL of the job posting to automatically extract details
                </p>
              </div>
              
              <div>
                <label htmlFor="experienceLevel" className="block text-sm font-medium text-gray-700">
                  Experience Level
                </label>
                <select
                  id="experienceLevel"
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive Level</option>
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Job Description*
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                required
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700">
                Responsibilities (comma-separated)
              </label>
              <textarea
                id="responsibilities"
                name="responsibilities"
                rows={3}
                value={formData.responsibilities}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="Enter responsibilities, one per line"
              />
            </div>
            
            <div>
              <label htmlFor="requiredSkills" className="block text-sm font-medium text-gray-700">
                Required Skills (comma-separated)
              </label>
              <textarea
                id="requiredSkills"
                name="requiredSkills"
                rows={3}
                value={formData.requiredSkills}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="Enter required skills, one per line"
              />
            </div>
            
            <div>
              <label htmlFor="preferredSkills" className="block text-sm font-medium text-gray-700">
                Preferred Skills (comma-separated)
              </label>
              <textarea
                id="preferredSkills"
                name="preferredSkills"
                rows={3}
                value={formData.preferredSkills}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="Enter preferred skills, one per line"
              />
            </div>
            
            <div>
              <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700">
                Application Deadline
              </label>
              <input
                type="text"
                id="applicationDeadline"
                name="applicationDeadline"
                value={formData.applicationDeadline}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Job'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddJobPage;